// @avantia common-code
import { ensureDefined, getOrdinal } from '@avantia/client-and-server-utilities';
import { HpCustomValidatorFunction, HpCustomValidatorResult, HpFieldValue } from '@avantia/form-validation';
import {
  createControlId,
  destructureControlId,
  getIdentifierConfigDefaultValues,
  HpAnswerMap,
  HpAnswerValueTypes,
  HpFields,
  identifierConfigChanged
} from '@avantia/questionset-model';
import {
  HpAnswerExpressionFunction,
  HpDynamicFunctionProps,
  HpPrerequisiteThisReference
} from '@avantia/questionset-script-parser';
import { getAddressDetailAnswer } from './engineClient/reducers/addressLookupFeatureReducer';
import { formatDate } from './engineClient/tools/dateTools';
import { HpClientPrerequisiteThisReferenceClass } from './engineClient/tools/prerequisiteThisReference';
import { getAnswerValue, isAgent } from './engineClient/tools/utilities';
import {
  HpClientLookupConditionFunctionProps,
  HpClientLookupExpressionFunction,
  HpClientPrerequisiteThisReference,
  HpClientQuestion,
  HpClientQuestionLookupItemMap,
  HpClientSection,
  HpCreateCustomGroupHeadingFunction,
  HpCreateCustomGroupHeadingFunctionProps,
  HpCustomSectionTitle
} from './engineClient/types';
import { calculateInspectionCondition, isAddressHistoryValid } from './reducers/claimsQuestionSetReducerLibrary';
import { AddressDataBeforeEditModel, createDataForAddress } from './repeatingGroupGetDataBeforeEdit';
import { coverTypeMap, getCoverTypeCode, isValidAddonTypeCode } from './tools/utilities';
import { AddonType, floodOtherReasonKey, perilTypeMap, Policy, PolicyMap } from './types/policyTypes';
import { questionIds } from './types/questionConstants';
import { fnolPhase } from './types/stateTypes';

const addressSummaryDateFormat = 'MMM YYYY';

let { maxGroupsAllowed } = getIdentifierConfigDefaultValues(); // just a temp default, it will be set correctly below.

identifierConfigChanged(({ maxGroupsAllowed: mga }) => {
  maxGroupsAllowed = mga;
});

function isCustomerFunction(this: HpPrerequisiteThisReference): boolean {
  const metadata = JSON.parse(this.getAnswerValue('metadata'));
  return !isAgent(metadata.agentId);
}

function isEnquiryPhaseVisibleFunction(this: HpPrerequisiteThisReference): boolean {
  return this.getAnswerValue('journeyPhase') !== fnolPhase;
}

function isFnolPhaseVisibleFunction(this: HpPrerequisiteThisReference): boolean {
  return this.getAnswerValue('journeyPhase') === fnolPhase;
}

function isSubmittingClaimWithNoDetailsFunction(this: HpPrerequisiteThisReference): boolean {
  const metadata = JSON.parse(this.getAnswerValue('metadata'));
  return (
    isAgent(metadata.agentId) &&
    !this.getAnswerValue('policyInstance') &&
    this.getAnswerValue('lossDate') &&
    !this.getAnswerValue('lastVisitToProperty')
  );
}

function createListOfPeopleFunction(
  this: HpClientPrerequisiteThisReference<HpClientQuestion>
): HpClientQuestionLookupItemMap {
  const map: HpClientQuestionLookupItemMap = {};
  const qFirstName = this.getQuestion({ questionId: 'firstName', questionType: 'question' });
  const qLastName = this.getQuestion({ questionId: 'lastName', questionType: 'question' });
  if (!qFirstName || !qLastName) {
    return map;
  }

  const firstNameId = qFirstName.questionId;
  const lastNameId = qLastName.questionId;
  for (let i = 1; i < maxGroupsAllowed; i++) {
    if (this.isElementVisible(firstNameId, i) && this.isElementVisible(lastNameId, i)) {
      const fname = this.getAnswerValue({ questionId: firstNameId, groupNumber: i, questionType: 'question' });
      const lname = this.getAnswerValue({ questionId: lastNameId, groupNumber: i, questionType: 'question' });
      if (fname && lname) {
        map[fname + ' ' + lname] = null;
      }
    } else {
      break;
    }
  }

  return map;
}

function createResidentsLookupOptionsFunction(
  this: HpClientPrerequisiteThisReference<HpClientQuestion>
): HpClientQuestionLookupItemMap {
  const map: HpClientQuestionLookupItemMap = { You: null, 'Other Resident': null };
  const fullNameCollection: [string, string, string][] = [
    ['jointNamesFirstName', 'jointNamesSurname', 'jointNamesDateOfBirth'],
    [
      'cancelledInsuranceOtherResidentFirstName',
      'cancelledInsuranceOtherResidentSurname',
      'cancelledInsuranceOtherResidentDateOfBirth'
    ],
    ['bankruptcyOtherResidentFirstName', 'bankruptcyOtherResidentSurname', 'bankruptcyOtherResidentDateOfBirth'],
    ['liabilityOtherResidentFirstName', 'liabilityOtherResidentSurname', 'liabilityOtherResidentDateOfBirth'],
    [
      'criminalConvictionOtherResidentFirstName',
      'criminalConvictionOtherResidentSurname',
      'criminalConvictionOtherResidentDateOfBirth'
    ]
  ];
  fullNameCollection.forEach((fullName) => {
    for (let i = 1; i < maxGroupsAllowed; i++) {
      const fname = this.getAnswerValue({ questionId: fullName[0], groupNumber: i, questionType: 'question' });
      const lname = this.getAnswerValue({ questionId: fullName[1], groupNumber: i, questionType: 'question' });
      const dateOfBirth = this.getAnswerValue({ questionId: fullName[2], groupNumber: i, questionType: 'question' });
      if (fname && lname && dateOfBirth) {
        map[`${fname} ${lname} - ${(dateOfBirth as string).substring(0, 10)}`] = null;
      } else {
        break;
      }
    }
  });
  return map;
}

export type HpAreAnyItemsInEditModeFunction = (
  name: string,
  thisRef: HpClientPrerequisiteThisReferenceClass<HpClientQuestion, HpClientSection>
) => boolean;

const areAnyItemsInEditModeFunction = (
  name: string,
  thisRef: HpClientPrerequisiteThisReferenceClass<HpClientQuestion, HpClientSection>
): boolean => {
  const fullName = `${name}Edit`;
  const questionId = ensureDefined(questionIds[fullName], `questionIds['${fullName}']`);
  for (let i = 1; i < maxGroupsAllowed; i++) {
    const id = createControlId({ groupNumber: i, questionId, options: { noPrefix: true } });
    if (thisRef.getAnswerValue(id) === true) {
      return true;
    }
  }

  return false;
};

function hasAddressBeenSelectedValidatorFunction(
  this: HpPrerequisiteThisReference,
  fieldName: string,
  fieldValue: HpFieldValue,
  fields: HpFields
) {
  const { addressLookupResults, addressId, addressEnterManuallyButton } = questionIds;
  const { groupNumber } = destructureControlId(fieldName, { requirePrefix: false });
  const addressIdId = createControlId({ questionId: addressId, groupNumber, options: { noPrefix: true } });
  const addressEnterManuallyButtonId = createControlId({
    questionId: addressEnterManuallyButton,
    groupNumber,
    options: { noPrefix: true }
  });
  const showingResults = this.isElementVisible(addressLookupResults, groupNumber);
  const hasAddressId = fields.get(addressIdId).value;
  const usingLookup = fields.get(addressEnterManuallyButtonId).value !== 'true';
  const isOk = !usingLookup || showingResults || (usingLookup && hasAddressId);
  return !!isOk;
}

function isMovedInDateBeforeMovedOutDateFunction(
  this: HpPrerequisiteThisReference,
  fieldName: string,
  fieldValue: HpFieldValue,
  fields: HpFields
): boolean {
  const { movedInDate: movedInDateQid } = questionIds;
  const { groupNumber } = destructureControlId(fieldName, { requirePrefix: false });
  const movedInDateId = createControlId({ questionId: movedInDateQid, groupNumber, options: { noPrefix: true } });
  const movedInDateValue = fields.get(movedInDateId).value as string;
  const movedOutDateValue = fields.get(fieldName).value as string;
  if (!movedInDateValue || !movedOutDateValue) {
    return true;
  }

  const movedInDate = new Date(movedInDateValue);
  const movedOutDate = new Date(movedOutDateValue);
  return movedInDate <= movedOutDate;
}

// The address they say they are still at has no move-out date to give. This passes rather than stopping the chain,
// so an error recorded by an earlier save is cleared when they answer yes.
function isMovedOutDateCompleteFunction(
  this: HpPrerequisiteThisReference,
  fieldName: string,
  fieldValue: HpFieldValue,
  fields: HpFields
): boolean {
  const { isCurrentAddress: isCurrentAddressQid } = questionIds;
  const { groupNumber } = destructureControlId(fieldName, { requirePrefix: false });
  const isCurrentAddressId = createControlId({
    questionId: isCurrentAddressQid,
    groupNumber,
    options: { noPrefix: true }
  });
  const isCurrentAddressField = fields.get(isCurrentAddressId);
  if (isCurrentAddressField && String(isCurrentAddressField.value) === 'true') {
    return true;
  }

  const movedOutDateField = fields.get(fieldName);
  if (!movedOutDateField) {
    return false;
  }

  const dateParts = String(movedOutDateField.value)
    .split('-')
    .filter((part) => part.length > 0);
  return dateParts.length === 3;
}

function isNotOverlappingDateFunction(
  this: HpPrerequisiteThisReference,
  fieldName: string,
  fieldValue: HpFieldValue
): boolean {
  const {
    movedInDate: movedInDateQid,
    movedOutDate: movedOutDateQid,
    isCurrentAddress: isCurrentAddressQid
  } = questionIds;
  const { questionId, groupNumber } = destructureControlId(fieldName, { requirePrefix: false });
  const selectedDate = new Date(fieldValue as string);
  for (let i = 1; i < maxGroupsAllowed; i++) {
    if (i === groupNumber) {
      continue;
    }

    const movedInDateValue = this.getAnswerValue({
      questionId: movedInDateQid,
      groupNumber: i,
      questionType: 'question'
    });
    if (!movedInDateValue) {
      continue;
    }

    const movedOutDateValue = this.getAnswerValue({
      questionId: movedOutDateQid,
      groupNumber: i,
      questionType: 'question'
    });
    const isCurrentAddress =
      this.getAnswerValue({ questionId: isCurrentAddressQid, groupNumber: i, questionType: 'question' }) === true;
    // The address they say they are still at runs to today. Without a move-out date and without saying so, how
    // long they were there is unknown, so there is nothing to overlap with.
    if (!movedOutDateValue && !isCurrentAddress) {
      continue;
    }

    const movedOutDate = movedOutDateValue ? new Date(movedOutDateValue as string) : new Date();
    if (questionId === movedInDateQid) {
      if (selectedDate > new Date(movedInDateValue) && selectedDate < movedOutDate) {
        return false;
      }
    } else {
      if (selectedDate > new Date(movedInDateValue) && selectedDate <= movedOutDate) {
        return false;
      }
    }
  }

  return true;
}

function setCurrentAddressDefaultFunction(
  this: HpPrerequisiteThisReference,
  { questionId }: HpDynamicFunctionProps
): false | null {
  const hasCurrentAddressBeenAdded = hasCurrentAddressBeenAddedFunction.call(this, {
    questionId
  } as HpDynamicFunctionProps);
  if (hasCurrentAddressBeenAdded) {
    return false;
  }

  return null;
}

function hasCurrentAddressBeenAddedFunction(
  this: HpPrerequisiteThisReference,
  { questionId }: HpDynamicFunctionProps
): boolean {
  const { isCurrentAddress: isCurrendAddressQid } = questionIds;
  let hasCurrentAddressBeenAdded = false;
  const { groupNumber } = destructureControlId(questionId, { requirePrefix: false });
  for (let i = 1; i < maxGroupsAllowed; i++) {
    const currentAddress = this.getAnswerValue({
      questionId: isCurrendAddressQid,
      groupNumber: i,
      questionType: 'question'
    });
    if (currentAddress) {
      if (i === groupNumber) {
        hasCurrentAddressBeenAdded = false;
        break;
      }

      hasCurrentAddressBeenAdded = true;
      break;
    }
  }

  return hasCurrentAddressBeenAdded;
}

function createAddressGroupSummaryTitleFunction({
  answers,
  questions,
  groupNumber,
  section
}: HpCreateCustomGroupHeadingFunctionProps): HpCustomSectionTitle {
  function getAnswer(questionId: string): HpAnswerValueTypes {
    const id = createControlId({ questionId, groupNumber, options: { noPrefix: true } });
    const answer = answers[id];
    if (answer) {
      const { answerValue } = getAnswerValue(answer, questions[id], 'control');
      return answerValue;
    }

    return null;
  }

  // const editId = questionIds.addressEdit;
  const {
    addressEdit,
    addressLine1,
    postcode,
    townCity: addressTownCity,
    movedInDate: addressMovedInDate,
    movedOutDate: addressMovedOutDate,
    policyholderFullName
  } = questionIds;

  const isEditing = getAnswer(addressEdit) === true;
  if (isEditing) {
    const fullName = getAnswerValue(answers[policyholderFullName], questions[policyholderFullName], 'control')
      .answerValue as string;

    const { heading } = section;
    const text = ensureDefined(heading, 'heading').replace('{{fullName}}', fullName);

    return {
      text,
      className: 'hp-address-history-title'
    };
  }

  const line1 = getAnswer(addressLine1) as string;
  const addressPostcode = getAnswer(postcode) as string;
  const townCity = getAnswer(addressTownCity) as string;
  const movedInDate = getAnswer(addressMovedInDate) as string;
  const movedOutDate = getAnswer(addressMovedOutDate) as string;

  if (!movedInDate) {
    return {
      text: '',
      className: ''
    };
  }

  // No move-out date means they have not moved out of this address.
  const formattedMovedOutDate = movedOutDate ? `${formatDate(movedOutDate, addressSummaryDateFormat)}` : 'present';
  const formattedDates = `${formatDate(movedInDate, addressSummaryDateFormat)} - ${formattedMovedOutDate}`;
  return {
    text: `<strong>${formattedDates}</strong> ${line1} ${townCity} ${addressPostcode}`,
    className: 'hp-group-section-collapsed'
  };
}

function isAddonAvailableFunction(
  this: HpPrerequisiteThisReference,
  { lookupKey }: HpClientLookupConditionFunctionProps
): boolean {
  const selectedPolicyInstance = getSelectedPolicyInstance(this);
  if (!selectedPolicyInstance) {
    return false;
  }

  const addonDetails = selectedPolicyInstance.addonDetails;
  // TODO AL - Change to ensureValidAddon
  const isValidAddon = isValidAddonTypeCode(lookupKey as AddonType);
  if (isValidAddon) {
    return !!addonDetails.availableAddons[lookupKey];
  }

  return false;
}

function isSelectedCoverTypeFunction(
  this: HpPrerequisiteThisReference,
  { lookupKey }: HpClientLookupConditionFunctionProps
): boolean {
  const policyInstance = getSelectedPolicyInstance(this);
  if (!policyInstance) {
    return false;
  }

  const { coverType } = policyInstance;
  // TODO AL - Cover type should already come mapped from Pas Interface.
  const coverTypeCode = getCoverTypeCode(coverType);
  // For BuildingsAndContents we show all covers.
  if (coverTypeCode === coverTypeMap.BuildingsAndContents) {
    return true;
  }

  return lookupKey === coverTypeCode;
}

export function getSelectedPolicyInstance(thisRef: HpPrerequisiteThisReference): Policy | undefined {
  const policies = JSON.parse(thisRef.getAnswerValue(questionIds.policyData) as string) as PolicyMap;
  const policyReference = thisRef.getAnswerValue(questionIds.policyInstance) as string;
  return policies && policies[policyReference];
}

function isVisibleForAccidentalLossJourneyFunction(this: HpPrerequisiteThisReference): boolean {
  const perilType = this.getAnswerValue('perilType');
  const isCustomer = isCustomerFunction.call(this);
  return (!isCustomer && perilType === perilTypeMap.AccidentalLoss) || perilType !== perilTypeMap.AccidentalLoss;
}

/** Flood has no perilType tile of its own, so it is identified by perilType 'Other' plus otherReason 'Flood'. */
function isFloodSelected(answers: HpAnswerMap): boolean {
  const perilTypeAnswer = answers[questionIds.perilType];
  const otherReasonAnswer = answers[questionIds.otherReason];
  const perilType = perilTypeAnswer ? perilTypeAnswer.customer : undefined;
  const otherReason = otherReasonAnswer ? otherReasonAnswer.customer : undefined;
  return perilType === perilTypeMap.Other && otherReason === floodOtherReasonKey;
}

function createIncidentDetailsTitleFunction({ answers }: HpCreateCustomGroupHeadingFunctionProps): string {
  return isFloodSelected(answers) ? 'Flood incident details' : 'Leak details';
}

function createDamagedRoomTitleFunction({ groupNumber, answers }: HpCreateCustomGroupHeadingFunctionProps): string {
  const { groupStartNumber } = getIdentifierConfigDefaultValues();
  const natural = (groupNumber || 0) + (groupStartNumber === 0 ? 1 : 0);
  const ordinal = getOrdinal(natural, { lowerCase: false });
  return `${ordinal} Damaged ${isTheftSelected(answers) ? 'Area' : 'Room'}`;
}

function createIncidentIntroSubheadingFunction({ answers }: HpCreateCustomGroupHeadingFunctionProps): string {
  return isTheftSelected(answers)
    ? 'Tell us where the incident happened and what you need to claim for.'
    : 'Tell us what has been damaged.';
}

function isTheftSelected(answers: HpAnswerMap): boolean {
  const perilTypeAnswer = answers[questionIds.perilType];
  return (perilTypeAnswer ? perilTypeAnswer.customer : undefined) === perilTypeMap.Theft;
}

function createIncidentDetailsSubheadingFunction({ answers }: HpCreateCustomGroupHeadingFunctionProps): string {
  return isFloodSelected(answers)
    ? 'Tell us about the flooding and the water that entered the property.'
    : 'Tell us about the source and nature of the leak.';
}

function anyRoomItemsContain(
  this: HpClientPrerequisiteThisReference<HpClientQuestion>,
  value: string,
  questionIdOrName?: string
): boolean {
  const targetQuestion = questionIdOrName || 'itemsDamaged';
  for (let i = 1; i < maxGroupsAllowed; i++) {
    const items = this.getAnswerValue({
      questionId: targetQuestion,
      groupNumber: i,
      questionType: 'question'
    });
    if (Array.isArray(items) && items.indexOf(value) >= 0) {
      return true;
    }
  }

  return false;
}

function calculateInspectionConditionFunction(this: HpPrerequisiteThisReference): boolean | null {
  const lossDate = this.getAnswerValue('lossDate') as string | null;
  const lastVisit = this.getAnswerValue('lastVisitToProperty') as string | null;
  return calculateInspectionCondition(lossDate, lastVisit);
}

/**
 * How a policyholder's full name is composed. Exported so anything filling the answer in without the browser
 * composes it the same way rather than keeping a second copy of the format.
 */
export function composeFullName(forename: string, surname: string): string {
  return `${forename} ${surname}`;
}

function createFullNameFunction(this: HpPrerequisiteThisReference) {
  return composeFullName(String(this.getAnswerValue('forename')), String(this.getAnswerValue('surname')));
}

export function getDataInitialFunction(): HpAnswerValueTypes {
  const res: AddressDataBeforeEditModel = {
    showCancel: false,
    showDelete: true,
    deleteIsCancel: true
  };
  return res as any;
}

export function getAddressDataBeforeEdit(this: HpPrerequisiteThisReference): HpAnswerValueTypes {
  return createDataForAddress({ action: 'edit', name: 'address', self: this }) as any;
}

export function getAddressDataAfterSave(this: HpPrerequisiteThisReference): HpAnswerValueTypes {
  return createDataForAddress({ action: 'save', name: 'address', self: this }) as any;
}

function getGroupAnswerValueFunction(this: HpPrerequisiteThisReference, fieldName: string, questionName): boolean {
  const qid = questionIds[questionName];
  const { groupNumber } = destructureControlId(fieldName, { requirePrefix: false });
  const questionControl = createControlId({
    questionId: qid,
    groupNumber,
    options: { noPrefix: true }
  });
  const answer = this.getAnswerValue(questionControl);

  return !!answer;
}

function createAccountHolderNameLookupOptions(this: HpPrerequisiteThisReference): HpClientQuestionLookupItemMap {
  const selectedPolicyInstance = getSelectedPolicyInstance(this);
  if (!selectedPolicyInstance) {
    return {};
  }

  const { forename, surname, additionalPolicyHolders = [] } = selectedPolicyInstance;
  const mainPolicyHolderName = `${forename} ${surname}`;
  const map: HpClientQuestionLookupItemMap = {
    mainPH: { text: mainPolicyHolderName }
  };

  if (additionalPolicyHolders.length > 0) {
    additionalPolicyHolders.forEach((phDetails, index) => {
      const { forename: jphForename, surname: jphSurname } = phDetails;
      map[`jph${index + 1}`] = { text: `${jphForename} ${jphSurname}` };
    });
  }

  map['someoneElse'] = { text: 'Someone else' };

  return map;
}

export type HpGetGroupQuestionAnswerFunction = (
  this: HpPrerequisiteThisReference,
  fieldName: string,
  questionName: string
) => boolean;

export type HpClientLookupConditionFunction = (
  this: HpPrerequisiteThisReference,
  props: HpClientLookupConditionFunctionProps
) => boolean;

export type HpValidateAddressHistoryFunction = (thisRef: HpPrerequisiteThisReference) => HpCustomValidatorResult;

// Ensure that we have the correct function signature.
const createListOfPeople: HpClientLookupExpressionFunction = createListOfPeopleFunction;
const createResidentsLookupOptions: HpClientLookupExpressionFunction = createResidentsLookupOptionsFunction;
const getAddressDetail: HpAnswerExpressionFunction = getAddressDetailAnswer;
const isAddonAvailable: HpClientLookupConditionFunction = isAddonAvailableFunction;
const isSelectedCoverType: HpClientLookupConditionFunction = isSelectedCoverTypeFunction;
const isCustomer: HpAnswerExpressionFunction = isCustomerFunction;
const isEnquiryPhaseVisible: HpAnswerExpressionFunction = isEnquiryPhaseVisibleFunction;
const isFnolPhaseVisible: HpAnswerExpressionFunction = isFnolPhaseVisibleFunction;
// Returns a plain string rather than an HpCustomSectionTitle so the section keeps its standard (non-collapsible) styling.
const createIncidentDetailsTitle = createIncidentDetailsTitleFunction;
const createIncidentDetailsSubheading = createIncidentDetailsSubheadingFunction;
const createIncidentIntroSubheading = createIncidentIntroSubheadingFunction;
const createDamagedRoomTitle = createDamagedRoomTitleFunction;
const isSubmittingClaimWithNoDetails: HpAnswerExpressionFunction = isSubmittingClaimWithNoDetailsFunction;
const isVisibleForAccidentalLossJourney: HpAnswerExpressionFunction = isVisibleForAccidentalLossJourneyFunction;
const createFullName: HpAnswerExpressionFunction = createFullNameFunction;
const getDataInitial: HpAnswerExpressionFunction = getDataInitialFunction;
const calculateInspection: HpAnswerExpressionFunction = calculateInspectionConditionFunction;
const areAnyItemsInEditMode: HpAreAnyItemsInEditModeFunction = areAnyItemsInEditModeFunction;
const hasAddressBeenSelectedValidator: HpCustomValidatorFunction = hasAddressBeenSelectedValidatorFunction as any; // TODO - fix type;
const createAddressGroupSummaryTitle: HpCreateCustomGroupHeadingFunction = createAddressGroupSummaryTitleFunction;
const isMovedInDateBeforeMovedOutDate: HpCustomValidatorFunction = isMovedInDateBeforeMovedOutDateFunction as any; // TODO - fix type;
const isMovedOutDateComplete: HpCustomValidatorFunction = isMovedOutDateCompleteFunction as any; // TODO - fix type;
const getGroupAnswerValue: HpGetGroupQuestionAnswerFunction = getGroupAnswerValueFunction;
const isNotOverlappingDate: HpCustomValidatorFunction = isNotOverlappingDateFunction as any; // TODO - fix type;
const hasCurrentAddressBeenAdded: HpAnswerExpressionFunction = hasCurrentAddressBeenAddedFunction;
const setCurrentAddressDefault: HpAnswerExpressionFunction = setCurrentAddressDefaultFunction;
const createAccountHolderNameLookups: HpClientLookupExpressionFunction = createAccountHolderNameLookupOptions;
const validateAddressHistory: HpValidateAddressHistoryFunction = isAddressHistoryValid;

export {
  anyRoomItemsContain,
  areAnyItemsInEditMode,
  calculateInspection,
  createAccountHolderNameLookups,
  createAddressGroupSummaryTitle,
  createDamagedRoomTitle,
  createFullName,
  createIncidentDetailsSubheading,
  createIncidentDetailsTitle,
  createIncidentIntroSubheading,
  createListOfPeople,
  createResidentsLookupOptions,
  getAddressDetail,
  getDataInitial,
  getGroupAnswerValue,
  hasAddressBeenSelectedValidator,
  hasCurrentAddressBeenAdded,
  isAddonAvailable,
  isCustomer,
  isEnquiryPhaseVisible,
  isFnolPhaseVisible,
  isMovedInDateBeforeMovedOutDate,
  isMovedOutDateComplete,
  isNotOverlappingDate,
  isSelectedCoverType,
  isSubmittingClaimWithNoDetails,
  isVisibleForAccidentalLossJourney,
  setCurrentAddressDefault,
  validateAddressHistory
};
